export type AutomaticSync = {
  enabled: boolean;
  times: string[];
  timeZone: string;
  pausedUntil?: string | null;
  nextCheckAt?: string | null;
  recoveryPending?: boolean;
  recoveryIntervalText?: string;
  blockedReason?: string | null;
  intervalMinutes?: number;
  intervalSeconds?: number;
};

export function sourceTime(value?: string | null, timeZone = 'Asia/Kolkata'): string | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  try {
    return `${new Date(value).toLocaleString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone })} ${timeZone === 'Asia/Kolkata' ? 'IST' : timeZone}`;
  } catch {
    return null;
  }
}

export function describeSync(automatic: AutomaticSync | undefined, configured: boolean) {
  const blocked = !configured
    ? 'not_configured'
    : automatic?.blockedReason || (automatic && !automatic.enabled ? 'disabled' : null);
  const reasons: Record<string, string> = {
    not_configured: 'Complete and verify the source settings before automatic checks can run.',
    disabled: 'Automatic sync is not enabled.',
    source_paused: 'The source is paused. Resume it before automatic checks can run.',
    owner_unavailable: 'An active source owner is required before automatic checks can run.',
  };
  const next = automatic && !blocked ? sourceTime(automatic.nextCheckAt, automatic.timeZone) : null;
  const times = automatic?.times?.filter((time) => /^\d{2}:\d{2}$/.test(time)).join(' and ');
  const interval =
    automatic?.intervalMinutes || (automatic?.intervalSeconds ? automatic.intervalSeconds / 60 : 0);
  return {
    title: !configured
      ? 'Source setup required'
      : !automatic
        ? 'Automatic sync status unavailable'
        : blocked
          ? 'Automatic checks are blocked'
          : automatic.recoveryPending
            ? 'Waiting for source access'
            : 'Automatic source checks',
    schedule: automatic
      ? automatic.recoveryIntervalText ||
        (interval
          ? `Every ${interval} minute${interval === 1 ? '' : 's'}`
          : times
            ? `Daily at ${times} ${automatic.timeZone === 'Asia/Kolkata' ? 'IST' : automatic.timeZone}`
            : 'Schedule not supplied')
      : 'Schedule not supplied',
    next: next ? `Next scheduled check: ${next}.` : 'No automatic check is currently scheduled.',
    reason:
      !automatic && configured
        ? 'The server has not supplied automatic-sync status.'
        : blocked
          ? reasons[blocked] ||
            `Automatic checks require attention: ${blocked.replaceAll('_', ' ')}.`
          : 'Checks run while this computer and the CRM API are running and online.',
    recovery: automatic?.recoveryPending
      ? 'The source last reported an access or quota limit. The next permitted scheduled check will try again; successful access allows sync to resume.'
      : '',
  };
}
